// "Send rota to engineers" (Session ATU). The office's ADVANCE notice of the standby rota: one branded
// PDF of the coming weeks, and one email per engineer naming their own weeks.
//
// THE PREVIEW IS THE CONFIRMATION, and it names every recipient by name AND address before anything
// is sent (the branded-confirm rule: a send names who it goes to). An engineer with no email is shown
// as skipped and never blocks the others; if nobody has one, Send is disabled and the reason is said.
// After a send, every engineer's outcome is listed, and sending again simply sends again.
//
// NOTHING IS SENT FROM HERE WITHOUT A PRESS OF SEND. The page opens this after a rota save only when
// the office answers "Send now" to the offer; saving never sends.
//
// Session AUV (design system Session 8): the loader and the dates are the shared Loader and lib/format.ts (a week prints
// "28 Sep 2026" where it printed "28 September 2026"), and the outcome word's colour is toneTextClass. Presentation only.
import { useCallback, useEffect, useState } from "react";
import { ApiError, api, STANDBY_NOTICE_WEEK_OPTIONS } from "../../lib/api";
import type {
  StandbyNoticePreview,
  StandbyNoticeResult,
  StandbyNoticeWeeks,
} from "../../lib/api";
import {
  ModalShell,
  MODAL_ACTIONS,
  MODAL_PRIMARY_BTN,
  MODAL_SECONDARY_BTN,
} from "../../components/ModalShell";
import { presentableServerMessage } from "../../lib/serverMessage";
import { Loader } from "../../components/Loader";
import { toneTextClass } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";

export const NO_EMAIL_LABEL = "No email on file, will be skipped";

/** How one engineer's send result reads. Pure. */
export function noticeOutcomeLabel(r: StandbyNoticeResult["results"][number]): {
  text: string;
  tone: "ok" | "warn" | "quiet";
} {
  if (r.outcome === "sent") return { text: "Sent", tone: "ok" };
  if (r.outcome === "skipped_no_email")
    return { text: "Skipped: no email on file", tone: "quiet" };
  return {
    text: `Not sent. ${r.reason ?? "The email could not be sent."}`,
    tone: "warn",
  };
}

/** "28 Sep 2026 to 20 Dec 2026" for the weeks covered (Monday of the first to Sunday of the last). Pure. */
export function noticeRangeLabel(fromWeek: string, lastWeek: string): string {
  const end = new Date(`${lastWeek}T12:00:00.000Z`);
  end.setUTCDate(end.getUTCDate() + 6);
  return `${formatDate(fromWeek)} to ${formatDate(end.toISOString().slice(0, 10))}`;
}

function errText(err: unknown, fallback: string): string {
  return (
    (err instanceof ApiError ? presentableServerMessage(err.message) : null) ??
    fallback
  );
}

// ok reads green, warn red, quiet grey: the shared tone ink, one place for each colour.
const outcomeInk = (tone: "ok" | "warn" | "quiet"): string => toneTextClass(tone === "ok" ? "green" : tone === "warn" ? "red" : "grey");

export function StandbyRotaSendModal({ onClose }: { onClose: () => void }) {
  const [weeks, setWeeks] = useState<StandbyNoticeWeeks>(12);
  const [preview, setPreview] = useState<StandbyNoticePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [result, setResult] = useState<StandbyNoticeResult | null>(null);

  const load = useCallback(async (w: StandbyNoticeWeeks) => {
    setPreview(null);
    setLoadError(null);
    try {
      setPreview(await api.standby.noticePreview(w));
    } catch (err) {
      setLoadError(
        errText(err, "Could not prepare the rota to send. Please try again."),
      );
    }
  }, []);

  useEffect(() => {
    void load(weeks);
  }, [load, weeks]);

  async function send() {
    setSending(true);
    setSendError(null);
    try {
      setResult(await api.standby.sendNotice(weeks));
    } catch (err) {
      setSendError(
        errText(err, "The rota could not be sent. Please try again."),
      );
    } finally {
      setSending(false);
    }
  }

  const withEmail = preview?.recipients.filter((r) => r.email) ?? [];
  const canSend = Boolean(
    preview && !preview.cannotSendReason && withEmail.length > 0,
  );

  return (
    <ModalShell
      ariaLabel="Send the standby rota to the engineers"
      onClose={() => !sending && onClose()}
      width="max-w-[600px]"
    >
      <h2 className="mb-1 font-heading text-xl tracking-wide text-ele-strong">
        Send rota to engineers
      </h2>
      <p className="mb-4 text-xs text-ele-body">
        Each engineer gets an email naming their own standby weeks, with the
        full rota attached as a PDF. Nothing is sent until you press Send.
      </p>

      {result ? (
        <div>
          <p className="mb-3 text-sm text-ele-strong">
            {result.sent === 1
              ? "Sent to 1 engineer"
              : `Sent to ${result.sent} engineers`}
            {result.skipped > 0 ? `, ${result.skipped} skipped` : ""}
            {result.failed > 0 ? `, ${result.failed} not sent` : ""}.
          </p>
          <ul
            className="rounded-ele border border-ele-purple-accent"
            aria-label="What happened for each engineer"
          >
            {result.results.map((r) => {
              const o = noticeOutcomeLabel(r);
              return (
                <li
                  key={r.engineerId}
                  className="flex flex-col gap-0.5 border-b border-ele-purple-accent px-3 py-2 text-sm last:border-b-0 sm:flex-row sm:justify-between sm:gap-3"
                >
                  <span className="text-ele-strong">{r.name}</span>
                  <span className={`text-xs ${outcomeInk(o.tone)}`}>{o.text}</span>
                </li>
              );
            })}
          </ul>
          <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
            <button
              type="button"
              onClick={() => setResult(null)}
              className={MODAL_SECONDARY_BTN}
            >
              Send again
            </button>
            <button
              type="button"
              onClick={onClose}
              className={MODAL_PRIMARY_BTN}
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <div>
          <label className="block">
            <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">
              Weeks to cover
            </span>
            <select
              value={weeks}
              onChange={(e) =>
                setWeeks(Number(e.target.value) as StandbyNoticeWeeks)
              }
              disabled={sending}
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green"
            >
              {STANDBY_NOTICE_WEEK_OPTIONS.map((w) => (
                <option key={w} value={w}>
                  Next {w} weeks, from this week
                </option>
              ))}
            </select>
          </label>

          {loadError && (
            <p
              role="alert"
              className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong"
            >
              {loadError}
            </p>
          )}
          {!preview && !loadError && (
            <Loader label="Loading the rota notice..." shape="inline" className="mt-3" />
          )}

          {preview && (
            <>
              <p className="mt-3 text-sm text-ele-body">
                Covers {noticeRangeLabel(preview.fromWeek, preview.lastWeek)}.{" "}
                <a
                  href={api.standby.noticePdfUrl(weeks)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-ele-green underline"
                >
                  Look at the PDF
                </a>
              </p>

              <span className="mt-4 mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">
                Who it goes to
              </span>
              <ul
                className="rounded-ele border border-ele-purple-accent"
                aria-label="Recipients"
              >
                {preview.recipients.length === 0 && (
                  <li className="px-3 py-2 text-sm text-ele-body">Nobody.</li>
                )}
                {preview.recipients.map((r) => (
                  <li
                    key={r.engineerId}
                    className="flex flex-col gap-0.5 border-b border-ele-purple-accent px-3 py-2 text-sm last:border-b-0 sm:flex-row sm:justify-between sm:gap-3"
                  >
                    <span className="text-ele-strong">{r.name}</span>
                    <span
                      className={`min-w-0 break-all text-xs ${r.email ? "text-ele-body" : "text-ele-danger"}`}
                    >
                      {r.email ?? NO_EMAIL_LABEL}
                    </span>
                  </li>
                ))}
              </ul>

              <span className="mt-4 mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">
                The weeks on the rota
              </span>
              <ul
                className="max-h-48 overflow-y-auto rounded-ele border border-ele-purple-accent text-sm"
                aria-label="Weeks on the rota"
              >
                {preview.rows.map((row) => (
                  <li
                    key={row.weekStarting}
                    className="flex justify-between gap-3 border-b border-ele-purple-accent px-3 py-1.5 last:border-b-0"
                  >
                    <span className="text-ele-body">
                      {formatDate(row.weekStarting)}
                    </span>
                    <span className="text-ele-strong">{row.onStandby}</span>
                  </li>
                ))}
              </ul>

              {preview.cannotSendReason && (
                <p
                  role="status"
                  className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong"
                >
                  {preview.cannotSendReason}
                </p>
              )}
            </>
          )}

          {sendError && (
            <p
              role="alert"
              className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong"
            >
              {sendError}
            </p>
          )}

          <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className={MODAL_SECONDARY_BTN}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void send()}
              disabled={!canSend || sending}
              className={MODAL_PRIMARY_BTN}
            >
              {sending
                ? "Sending..."
                : withEmail.length === 1
                  ? "Send to 1 engineer"
                  : `Send to ${withEmail.length} engineers`}
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}
